/**
 * Split a government buyer / supplier's registered name into a short, readable
 * display name and the legal form that wraps it.
 *
 * WHY. Registered org names in Uzbekistan lead (or trail) with a legal form that
 * eats the visible characters of a `<title>` and `<h1>` before the distinctive
 * part is reached — «Общество с ограниченной ответственностью "X"» (Russian,
 * PREFIX) or `"X" MAS'ULIYATI CHEKLANGAN JAMIYAT` (Uzbek-Latin, SUFFIX). On a
 * SERP the title truncates inside the legal form and every card looks identical
 * ("Общество с ограниченной ответст…"). This returns the distinctive name for
 * the title/h1 and hands the legal form back so a caller can keep it as
 * secondary text.
 *
 * Strategy, in order:
 *  1. If the name quotes its distinctive part — `"..."`, «...», or "..." — that
 *     quoted run IS the short name. This is how the registry writes the majority
 *     of Uzbek orgs, and it needs no legal-form dictionary to be correct.
 *  2. Otherwise strip known legal-form phrases and abbreviations off the front
 *     and back (ru + uz, Cyrillic + Latin), iteratively.
 *  3. If nothing usable survives, keep the original — never return an empty or
 *     one-character name.
 */

/** Result of {@link shortenOrgName}. `legalForm` is '' when none was removed. */
export interface OrgName {
  /** Distinctive name for the title/h1, e.g. `TRANSGAZINJINIRING`. */
  short: string;
  /** The stripped legal form, e.g. `MAS'ULIYATI CHEKLANGAN JAMIYAT`, or ''. */
  legalForm: string;
}

// Full legal-form phrases and their abbreviations, normalized (lowercase,
// apostrophe variants folded to ASCII "'"). Longest first so a full phrase is
// consumed before its abbreviation would match a fragment of it.
const FORMS: readonly string[] = [
  // — Banks (long, specific — kept first so «… AKSIYADORLIK TIJORAT BANKI»
  //   strips whole, instead of «aksiyadorlik jamiyati» clipping a fragment) —
  'акционерный коммерческий банк',
  'aksiyadorlik tijorat banki',
  'xususiy tijorat banki',
  'davlat tijorat banki',
  'акциядорлик тижорат банки',
  'хусусий тижорат банки',
  // — Russian / Cyrillic, full —
  'общество с ограниченной ответственностью',
  'открытое акционерное общество',
  'закрытое акционерное общество',
  'акционерное общество',
  'государственное унитарное предприятие',
  'дочернее унитарное предприятие',
  'унитарное предприятие',
  'государственное предприятие',
  'частное предприятие',
  'совместное предприятие',
  'семейное предприятие',
  'производственный кооператив',
  'фермерское хозяйство',
  // — Uzbek Cyrillic, full —
  'масъулияти чекланган жамияти',
  'масъулияти чекланган жамият',
  'очиқ акциядорлик жамияти',
  'ёпиқ акциядорлик жамияти',
  'акциядорлик жамияти',
  'давлат унитар корхонаси',
  'давлат корхонаси',
  'хусусий корхона',
  'оилавий корхона',
  'фермер хужалиги',
  'кушма корхона',
  'қўшма корхона',
  // — Uzbek Latin, full —
  "mas'uliyati cheklangan jamiyati",
  "mas'uliyati cheklangan jamiyat",
  'ochiq aksiyadorlik jamiyati',
  'yopiq aksiyadorlik jamiyati',
  'aksiyadorlik jamiyati',
  'davlat unitar korxonasi',
  'davlat korxonasi',
  'xususiy korxona',
  'oilaviy korxona',
  "fermer xo'jaligi",
  "qo'shma korxona",
  'yakka tartibdagi tadbirkor',
  // — Abbreviations (whole tokens) —
  'ооо',
  'оао',
  'зао',
  'ао',
  'гуп',
  'дуп',
  'уп',
  'гп',
  'чп',
  'сп',
  'фх',
  'мчж',
  'аж',
  'оаж',
  'ёаж',
  'дук',
  'хк',
  'ок',
  'mchj',
  'aj',
  'oaj',
  'duk',
  'xk',
  'fx',
  'yatt',
  'ytt',
];

/** Fold apostrophe variants (ʻ ' ` ´) to ASCII "'" for matching. */
function foldApostrophes(s: string): string {
  return s.replace(/[ʻ‘’`´]/g, "'");
}

/** Trim surrounding quotes, commas and whitespace left behind by stripping. */
function trimEdges(s: string): string {
  return s.replace(/^[\s,"'«»“”„-]+/u, '').replace(/[\s,"'«»“”„-]+$/u, '');
}

// The distinctive name inside quotes: double-quote, guillemets, or curly. Only
// these — NOT the ASCII apostrophe, which is a letter inside Uzbek words
// (o', g', MAS'ULIYATI).
const QUOTED_CORE = /"([^"]+)"|«([^»]+)»|“([^”]+)”/u;

/**
 * Is `boundaryChar` a place a legal-form token can legitimately end/begin — a
 * space, comma, quote, or the string edge? Guards against `АО` matching the
 * start of `АОЗТ` or a random word.
 */
function isBoundary(ch: string | undefined): boolean {
  return ch === undefined || /[\s,"'«»“”„-]/u.test(ch);
}

export function shortenOrgName(name: string | null | undefined): OrgName {
  const original = (name ?? '').replace(/\s+/g, ' ').trim();
  if (original.length < 2) return { short: original, legalForm: '' };

  // 1) Quoted distinctive name wins outright.
  const m = QUOTED_CORE.exec(original);
  if (m) {
    const core = (m[1] ?? m[2] ?? m[3] ?? '').replace(/\s+/g, ' ').trim();
    if (core.length >= 2) {
      const legalForm = trimEdges(original.replace(m[0], ' '));
      return { short: core, legalForm };
    }
  }

  // 2) No quotes — strip legal-form phrases off both ends. Work on a folded,
  //    lowercased copy for matching but slice the ORIGINAL (whitespace already
  //    collapsed, so positions align 1:1 — apostrophe folding is char-for-char).
  let start = 0;
  let end = original.length;
  const cmp = foldApostrophes(original.toLowerCase());
  const removed: string[] = [];

  let changed = true;
  while (changed && start < end) {
    changed = false;
    const head = cmp.slice(start, end);
    for (const form of FORMS) {
      // leading
      if (head.startsWith(form) && isBoundary(head[form.length])) {
        removed.push(original.slice(start, start + form.length));
        start += form.length;
        // consume the boundary run so the next iteration sees a clean head
        while (start < end && isBoundary(cmp[start])) start += 1;
        changed = true;
        break;
      }
      // trailing
      if (head.endsWith(form) && isBoundary(head[head.length - form.length - 1])) {
        const at = end - form.length;
        removed.unshift(original.slice(at, end));
        end = at;
        while (end > start && isBoundary(cmp[end - 1])) end -= 1;
        changed = true;
        break;
      }
    }
  }

  const short = trimEdges(original.slice(start, end));
  if (short.length < 2) return { short: original, legalForm: '' };
  return { short, legalForm: trimEdges(removed.join(' ')) };
}
